import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HexAdministration, HexServingMode, TreatmentInstruction } from 'src/app/api/models';
import { MedicalHistoryHelper } from 'src/app/shared/helpers/medical-history.helper';
import { componentDestroyed, untilComponentDestroyed } from 'src/app/shared/helpers/rxjs.operator';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { CustomTranslationService } from 'src/app/shared/translation/custom-translation.service';
import { Languages } from 'src/app/shared/translation/languages.enum';
import { DateTimeHelper } from "../../../shared/helpers/datetime.helper";

@Component({
  selector: 'diana-add-dosis',
  templateUrl: './add-dosis.component.html',
  styleUrls: ['./add-dosis.component.scss'],
  standalone: false
})
export class AddDosisComponent implements OnInit, OnDestroy, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() servingModes: HexServingMode[] | undefined = [];
  @Input() treatmentUnit: string = '';
  @Input() visitAt!: string;
  @Input() idHtmlElements: string = '';
  @Input() showApplicationType = false;
  @Input() applicationTypes: Array<HexAdministration> = [];

  get dosages(): FormArray {
    return this.formGroup.get('dosages') as FormArray;
  }
  get milkWithdrawalPeriodControl(): FormControl {
    return this.formGroup.get('milkWithdrawalPeriod') as FormControl;
  }
  get meatWithdrawalPeriodControl(): FormControl {
    return this.formGroup.get('meatWithdrawalPeriod') as FormControl;
  }
  get organWithdrawalPeriodControl(): FormControl {
    return this.formGroup.get('organWithdrawalPeriod') as FormControl;
  }
  get injectionPointWithdrawalPeriodControl(): FormControl {
    return this.formGroup.get('injectionPointWithdrawalPeriod') as FormControl;
  }

  @Input() treatmentInstruction!: TreatmentInstruction;
  @Output() durationInDaysChanged = new EventEmitter<Date>();
  @Output() amountChanged = new EventEmitter<number>();

  public currentLanguage = Languages.de;
  public Languages = Languages;

  constructor(
    private fb: FormBuilder,
    private localStorageService: LocalStorageService,
    private translationService: CustomTranslationService,
  ) {
    this.currentLanguage = this.localStorageService.getLanguage() || Languages.de;
    this.translationService.onLangChange.subscribe(() => {
      this.currentLanguage = this.localStorageService.getLanguage() || Languages.de;
    })
  }

  ngOnInit(): void {
    const isDisabled = this.dosages.disabled;
    this.dosages.clear();
    if (this.treatmentInstruction) {
      // adding the first line of a dosage in the form
      this.addDosagesDetails();
      // setting data on the first and second dosage
      if (this.treatmentInstruction.administrationId && this.applicationTypes && this.showApplicationType) {
        const applicationTypeSelected = this.applicationTypes.find(applicationType => applicationType.id === this.treatmentInstruction.administrationId);
        this.dosages.controls[0].get('applicationType')?.setValue(applicationTypeSelected || '');
      }
      if (this.treatmentInstruction.firstServingModeId && this.servingModes) {
        const servingModeSelected = this.servingModes.find(serving => serving.id === this.treatmentInstruction.firstServingModeId);
        this.dosages.controls[0].get('servingMode')?.setValue(servingModeSelected || '');
      }
      this.dosages.controls[0].get('amount')?.setValue(this.treatmentInstruction.firstDosage !== undefined ? this.treatmentInstruction.firstDosage : '');
      this.dosages.controls[0].get('durationInDays')?.setValue(this.treatmentInstruction.firstDuration !== undefined ? this.treatmentInstruction.firstDuration : '');
      if ((this.treatmentInstruction.secondServingModeId && this.servingModes) || this.treatmentInstruction.secondDosage || this.treatmentInstruction.secondDuration) {
        this.addDosagesDetails();
        this.dosages.controls[1].get('amount')?.setValue(this.treatmentInstruction.secondDosage !== undefined ? this.treatmentInstruction.secondDosage : '');
        this.dosages.controls[1].get('durationInDays')?.setValue(this.treatmentInstruction.secondDuration !== undefined ? this.treatmentInstruction.secondDuration : '');
      }
      if (this.treatmentInstruction.secondServingModeId && this.servingModes) {
        // adding second dosage in the form
        const servingModeSelected = this.servingModes.find(serving => serving.id === this.treatmentInstruction.secondServingModeId);
        this.dosages.controls[1].get('servingMode')?.setValue(servingModeSelected || '');
      }
      if (isDisabled) {
        this.dosages.disable();
      }
      // Fix to update duration and total after adding
      this.handleDurationInDaysChange();
      this.amountUpdated()
    } else {
      this.addDosagesDetails();
    }
    this.dosages.markAsPristine();

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visitAt'] && !changes['visitAt'].firstChange) {
      this.dosages.markAsDirty();
      this.handleDurationInDaysChange();
    }
  }


  ngOnDestroy(): void {
    componentDestroyed(this);
  }

  /**
   * Setting and calculating the total amount based on the values in the dosage form
   */
  updateTotalAmount(): number {
    let totalAmount = this.dosages.controls.reduce((accumulator, item) => {
      return MedicalHistoryHelper.getTotalDosis(item.get('amount')?.value, item.get('durationInDays')?.value, item.get('servingMode')?.value.value, accumulator);
    }, 0);
    return totalAmount;
  }

  /**
 * Setting and calculating the durationInDays based on the values in the dosage form
 */
  updateVisistAt(): Date {
    let totalDurationDays = this.dosages.controls.reduce((accumulator, item) => {
      if (item.get('durationInDays')) {
        return accumulator + Number(item.get('durationInDays')?.value);
      }
      return accumulator;
    }, 0);
    totalDurationDays = totalDurationDays - 1;
    if ((totalDurationDays) > 0) {
      const copyVisitAt = DateTimeHelper.copyDate(this.visitAt);
      return DateTimeHelper.copyDate(copyVisitAt.setDate(copyVisitAt.getDate() + totalDurationDays));
    } else {
      return DateTimeHelper.copyDate(this.visitAt);
    }
  }

  /**
   * Adding an dosis to the form formArray also setting the listeners for durationInDays and amount changes
   */
  addDosagesDetails() {
    const form = {
      servingMode: new FormControl(''),
      amount: new FormControl(''),
      units: new FormControl({ value: this.treatmentUnit, disabled: true }),
      durationInDays: new FormControl(''),
      applicationType: new FormControl('')
    }

    if (this.showApplicationType) {
      form.servingMode.addValidators(Validators.required);
      form.amount.addValidators(Validators.required);
      form.durationInDays.addValidators(Validators.required);
    }
    this.dosages.push(this.fb.group(form));
    // Setting the subscribes to handle the calculation of durationInDays and the totalAmount on durantion and amount change in the form
    this.dosages.controls[this.dosages.controls.length - 1].get('durationInDays')?.valueChanges
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.handleDurationInDaysChange();
        this.amountUpdated()
      })
    this.dosages.controls[this.dosages.controls.length - 1].get('amount')?.valueChanges
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.amountUpdated()
      })
  }

  public amountUpdated(): void {
    this.amountChanged.emit(this.updateTotalAmount());
  }

  public compareMinValue(control: FormControl, min: number | undefined): boolean {
    const withdrawalValue = MedicalHistoryHelper.getNumber(control.value);
    if (withdrawalValue !== null && min !== undefined && MedicalHistoryHelper.getNumber(control.value) < min) {
      return true
    }

    return false;
  }

  public removeDosis(): void {
    this.dosages.removeAt(1);
    this.dosages.markAsDirty();
    this.handleDurationInDaysChange();
    this.amountUpdated()
  }

  private handleDurationInDaysChange(): void {
    // I am using the dirty flag to not update the endDate on the first component initialization since it would override de saved endDate
    if (this.dosages.dirty === true) {
      this.durationInDaysChanged.emit(this.updateVisistAt());
    }
  }
}
